import { useEffect, useState } from "react";
import { api, money, formatApiError } from "../lib/api";
import { PageHeader, Card, Modal, Field, inputClass, EmptyState, PrimaryButton } from "../components/common";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Scales, Plus, Trash, Sparkle, Trophy, ArrowClockwise, CheckCircle, Warning } from "@phosphor-icons/react";

export default function Concursos() {
  const { user } = useAuth();
  const canDelete = ["admin", "gerente_general"].includes(user?.role) || !!user?.can_delete;
  const [tenders, setTenders] = useState([]);
  const [selId, setSelId] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", scope: "" });
  const [quote, setQuote] = useState({ contractor_name: "", amount: "", notes: "" });
  const [qFile, setQFile] = useState(null);
  const [comparing, setComparing] = useState(false);

  const load = () => api.get("/tenders").then((r) => setTenders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sel = tenders.find((t) => t.id === selId);

  const createTender = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/tenders", form);
      toast.success("Concurso creado"); setOpen(false); setForm({ title: "", description: "", scope: "" });
      await load(); setSelId(r.data.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addQuote = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("contractor_name", quote.contractor_name);
    fd.append("amount", quote.amount);
    fd.append("notes", quote.notes);
    if (qFile) fd.append("file", qFile);
    try {
      await api.post(`/tenders/${selId}/quotes`, fd);
      toast.success("Cotización agregada"); setQuote({ contractor_name: "", amount: "", notes: "" }); setQFile(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delQuote = async (qid) => { try { await api.delete(`/quotes/${qid}`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const delTender = async (tid) => { if (!window.confirm("¿Eliminar concurso y sus cotizaciones?")) return; try { await api.delete(`/tenders/${tid}`); setSelId(null); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const compare = async () => {
    setComparing(true);
    try { await api.post(`/tenders/${selId}/compare`); toast.success("Comparativa generada por IA"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setComparing(false); }
  };

  const comp = sel?.comparison;

  return (
    <div>
      <PageHeader overline="Licitaciones" title="Concursos / Cotizaciones"
        action={<PrimaryButton onClick={() => setOpen(true)} data-testid="add-tender-btn"><Plus size={18} weight="bold" /> Nuevo concurso</PrimaryButton>} />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Lista */}
        <div className="lg:col-span-1 space-y-3">
          {tenders.length === 0 ? (
            <EmptyState icon={Scales} title="Sin concursos" subtitle="Crea un concurso y agrega cotizaciones para compararlas con IA." />
          ) : tenders.map((t) => (
            <button key={t.id} data-testid={`tender-${t.id}`} onClick={() => setSelId(t.id)}
              className={`w-full text-left p-4 rounded-md border transition-colors duration-200 ${selId === t.id ? "border-primary bg-orange-50/50" : "border-border hover:bg-muted"}`}>
              <div className="font-semibold">{t.title}</div>
              <div className="text-xs text-muted-foreground mt-1">{t.quotes?.length || 0} cotización(es){t.comparison ? " · comparado" : ""}</div>
            </button>
          ))}
        </div>

        {/* Detalle */}
        <div className="lg:col-span-2">
          {!sel ? (
            <Card className="p-8 text-center text-muted-foreground">Selecciona un concurso para ver sus cotizaciones.</Card>
          ) : (
            <div className="space-y-6">
              <Card className="p-6" data-testid="tender-detail">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="font-heading font-bold text-xl">{sel.title}</h2>
                    {sel.scope && <p className="text-sm text-muted-foreground mt-1">Alcance: {sel.scope}</p>}
                    {sel.description && <p className="text-sm mt-1">{sel.description}</p>}
                  </div>
                  {canDelete && <button onClick={() => delTender(sel.id)} className="text-muted-foreground hover:text-danger" title="Eliminar"><Trash size={18} /></button>}
                </div>

                {/* Cotizaciones */}
                <div className="mt-5">
                  <div className="overline mb-2">Cotizaciones</div>
                  {(sel.quotes || []).length === 0 ? <p className="text-sm text-muted-foreground">Aún sin cotizaciones.</p> : (
                    <div className="space-y-2">
                      {sel.quotes.map((q) => (
                        <div key={q.id} data-testid={`quote-${q.id}`} className="flex items-center justify-between border border-border rounded-sm px-3 py-2 text-sm">
                          <div>
                            <span className="font-semibold">{q.contractor_name}</span>
                            <span className="text-primary font-bold ml-3">{money(q.amount)}</span>
                            {q.pdf_path && <span className="text-xs text-muted-foreground ml-2">· PDF adjunto</span>}
                            {q.notes && <div className="text-xs text-muted-foreground">{q.notes}</div>}
                          </div>
                          {canDelete && <button onClick={() => delQuote(q.id)} className="text-muted-foreground hover:text-danger"><Trash size={16} /></button>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Agregar cotización */}
                <form onSubmit={addQuote} className="mt-4 grid sm:grid-cols-2 gap-3 border-t border-border pt-4">
                  <Field label="Contratista *"><input required data-testid="quote-name" className={inputClass} value={quote.contractor_name} onChange={(e) => setQuote({ ...quote, contractor_name: e.target.value })} /></Field>
                  <Field label="Monto (MXN) *"><input required type="number" step="0.01" data-testid="quote-amount" className={inputClass} value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} /></Field>
                  <Field label="Notas"><input className={inputClass} value={quote.notes} onChange={(e) => setQuote({ ...quote, notes: e.target.value })} /></Field>
                  <Field label="Cotización PDF (opcional)"><input type="file" accept="application/pdf" className={inputClass + " py-2"} onChange={(e) => setQFile(e.target.files[0])} /></Field>
                  <div className="sm:col-span-2"><PrimaryButton type="submit" data-testid="add-quote-btn"><Plus size={16} weight="bold" /> Agregar cotización</PrimaryButton></div>
                </form>
              </Card>

              {/* Comparativa */}
              <Card className="p-6" data-testid="tender-comparison">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2"><Scales size={20} weight="bold" className="text-slate-700" /><span className="font-heading font-bold text-xl">Comparativa</span></div>
                  <button onClick={compare} disabled={comparing} data-testid="compare-btn" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-sm px-3 py-1.5 transition-colors duration-200">
                    <ArrowClockwise size={15} weight="bold" className={comparing ? "animate-spin" : ""} /> {comparing ? "Analizando…" : (comp ? "Re-comparar" : "Comparar")}
                  </button>
                </div>
                {!comp ? (
                  <p className="text-muted-foreground text-sm">Agrega al menos 2 cotizaciones y presiona "Comparar" para armar el cuadro comparativo y recomendar la mejor opción por valor.</p>
                ) : (
                  <div>
                    {(comp.comparables === false || (comp.discrepancias || []).length > 0) && (
                      <div data-testid="comparability-alert" className="border border-amber-300 bg-amber-50 rounded-sm p-4 mb-4">
                        <div className="flex items-center gap-2 font-semibold text-amber-800"><Warning size={18} weight="fill" /> Cuidado: las cotizaciones podrían NO ser comparables</div>
                        {comp.alerta_comparabilidad && <p className="text-sm text-amber-900/90 mt-1">{comp.alerta_comparabilidad}</p>}
                        {(comp.discrepancias || []).length > 0 && (
                          <ul className="mt-2 space-y-1.5">
                            {comp.discrepancias.map((d, i) => (
                              <li key={i} data-testid={`discrepancy-${i}`} className="text-sm text-amber-900 flex items-start gap-2">
                                <span className="mt-0.5 inline-flex px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-amber-200 text-amber-900">{d.tipo}</span>
                                <span>{d.detalle}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    <div className="border border-border bg-muted/40 rounded-sm p-4 mb-4 flex items-start gap-3">
                      <Trophy size={22} weight="fill" className="text-slate-700 mt-0.5" />
                      <div>
                        <div className="font-bold text-foreground">Recomendación: {comp.mejor_opcion}</div>
                        <p className="text-sm text-muted-foreground mt-1">{comp.recomendacion}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      {(comp.ranking || []).map((r, i) => (
                        <div key={i} data-testid={`rank-${i}`} className={`rounded-sm p-4 border ${r.contratista === comp.mejor_opcion ? "border-slate-800 bg-slate-50" : "border-border"}`}>
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-foreground">{i + 1}. {r.contratista}</span>
                            <span className="font-bold text-foreground">{money(r.monto)}</span>
                          </div>
                          {typeof r.puntuacion === "number" && (
                            <div className="mt-2 flex items-center gap-2">
                              <div className="h-2 flex-1 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-slate-700" style={{ width: `${r.puntuacion}%` }} /></div>
                              <span className="text-xs text-muted-foreground w-10 text-right">{r.puntuacion}/100</span>
                            </div>
                          )}
                          {r.puntos_fuertes?.length > 0 && (
                            <ul className="mt-2 space-y-0.5">{r.puntos_fuertes.map((p, j) => <li key={j} className="flex items-start gap-1.5 text-xs text-muted-foreground"><CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5" />{p}</li>)}</ul>
                          )}
                          {r.riesgos?.length > 0 && (
                            <ul className="mt-1 space-y-0.5">{r.riesgos.map((p, j) => <li key={j} className="flex items-start gap-1.5 text-xs text-muted-foreground"><Warning size={13} weight="fill" className="text-amber-600 mt-0.5" />{p}</li>)}</ul>
                          )}
                        </div>
                      ))}
                    </div>
                    {comp.generated_at && <div className="text-xs text-muted-foreground mt-4">{new Date(comp.generated_at).toLocaleString("es-MX")}</div>}
                  </div>
                )}
              </Card>
            </div>
          )}
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Nuevo concurso / licitación">
        <form onSubmit={createTender} className="space-y-4">
          <Field label="Título *"><input required data-testid="tender-title" className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej. Suministro y colocación de piso porcelanato" /></Field>
          <Field label="Alcance"><input className={inputClass} value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} placeholder="Metros, especificaciones, entregables…" /></Field>
          <Field label="Descripción"><textarea className={inputClass + " h-24 py-2"} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <PrimaryButton type="submit" data-testid="save-tender-btn">Crear concurso</PrimaryButton>
        </form>
      </Modal>
    </div>
  );
}
